import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import ChatMessage, { Message } from './ChatMessage';
import { ArrowDownIcon } from './icons';

interface ChatContainerProps {
  messages: Message[];
  isLoading: boolean;
  isAnalyzingFiles?: boolean;
  isSidebarOpen?: boolean;
  conversationId?: string;
  agentId?: string;
  modelProvider?: string;
  modelName?: string;
  modelApiVersion?: string;
  onNudgeRetryWithSearch?: () => void;
  onNudgeEnableAuto?: () => void;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ messages, isLoading, isAnalyzingFiles, isSidebarOpen = false, conversationId, agentId, modelProvider, modelName, modelApiVersion, onNudgeRetryWithSearch, onNudgeEnableAuto }) => {
  const { colors } = useTheme();
  const [showScrollButton, setShowScrollButton] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const checkScrollPosition = () => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      setShowScrollButton(!isAtBottom);
    }
  };

  useEffect(() => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      chatContainer.addEventListener('scroll', checkScrollPosition);
      return () => chatContainer.removeEventListener('scroll', checkScrollPosition);
    }
  }, []);

  useEffect(() => {
    checkScrollPosition();
  }, [messages]);

  return (
    <div 
      ref={containerRef}
      style={{
        maxWidth: '800px',
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        position: 'relative'
      }}
    >
      {messages.map((message, index) => (
        <ChatMessage
          key={index}
          message={message}
          isLoading={isLoading && index === messages.length - 1}
          isLastMessage={index === messages.length - 1}
          isAnalyzingFiles={isAnalyzingFiles && index === messages.length - 1}
          conversationId={conversationId}
          agentId={agentId}
          messageIndex={index}
          modelProvider={modelProvider}
          modelName={modelName}
          modelApiVersion={modelApiVersion}
          onNudgeRetryWithSearch={onNudgeRetryWithSearch}
          onNudgeEnableAuto={onNudgeEnableAuto}
        />
      ))}

      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          style={{
            position: 'fixed',
            bottom: '180px',
            left: `calc(50vw + ${isSidebarOpen ? '160px' : '30px'})`,
            transform: 'translateX(-50%)',
            width: '50px',
            height: '50px',
            borderRadius: '25px',
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: colors.textPrimary,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateX(-50%) translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateX(-50%)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          }}
          aria-label="Scroll to latest message"
        >
          <ArrowDownIcon size={20} />
        </button>
      )}
    </div>
  );
};

export default React.memo(ChatContainer);